import i18n from "i18next";
import type { FC } from "react";
import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams, useSearchParams } from "react-router";

import type { CardProfileType } from "src/app/features/ApplicationProfile/subFeatures/CardProfile/types/CardProfileType";
import { usePromptDataDeleteMutation } from "src/app/features/ApplicationProfile/subFeatures/PromptData/services/server/PromptDataApi";
import type { PromptDataEntity } from "src/app/features/ApplicationProfile/subFeatures/PromptData/types/PromptDataEntity";
import { type ExtendedColumnsType, useUpdateTableDataMutation } from "../../../../../../../components";
import type { AntCustomFormField, CommonFieldAttributes } from "../../../../../../../components/Form/types/types";
import { showSuccessNotification, showWarningNotification } from "../../../../../../../components/NotificationAlerts";
import type { AlertActionPayload, ExtendedAlertActionPayload } from "../../../../../../../components/NotificationAlerts/types";
import { PropertyTable } from "../../../../../../../components/Table/components/PropertyTable";
import { displayEnumValue } from "../../../../../../../components/Table/components/PropertyTable/transformations/cellTransformations";
import { Actions } from "../../../../../../../components/Table/types/types";
import { getAssignedDataGroup } from "../../../../../../../core/router/auth";
import { ResourceName } from "../../../../../../../core/router/config/ResourceName";
import { BASE_PATH, FORM_ACTION } from "../../../../../../../core/router/types/routerTypes";
import type { Entity } from "../../../../../../../core/types/fe";
import { PromptDataSelector } from "./PromptDataSelector";

const getColumnsConfig = (t: Function): ExtendedColumnsType<PromptDataEntity>[] => {
    return [
        {
            title: t("applicationProfileCreateForm.table.id"),
            key: "id",
            dataIndex: "id",
        },
        {
            title: t("applicationProfile.promptDataListCreateForm.table.name"),
            key: "name",
            dataIndex: "name",
        },
        {
            title: t("applicationProfile.promptDataListCreateForm.table.type"),
            key: "promptDataType",
            dataIndex: "promptDataType",
            render: (value) => displayEnumValue(value, "prompt-data-type"),
        },
        {
            title: t("applicationProfile.promptDataListCreateForm.table.code"),
            key: "promptDataCode",
            dataIndex: "promptDataCode",
        },
        {
            title: t("applicationProfile.promptDataListCreateForm.table.serviceLevel"),
            key: "serviceLevel",
            dataIndex: "serviceLevel",
        },
    ];
};

export interface AppProfilePromptDataTableProps extends AntCustomFormField<PromptDataEntity[]>, CommonFieldAttributes {}

export const AppProfilePromptDataTable: FC<AppProfilePromptDataTableProps> = ({ value = [], onChange, readOnly }) => {
    const { appProfileId, id } = useParams();
    const [searchParams] = useSearchParams();
    const type = searchParams.get("type") as CardProfileType;

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const resourceUrl = `${ResourceName.APPLICATION_PROFILE_LISTS}/${appProfileId}/${ResourceName.APPLICATION_PROFILE_CARD_DEFINITION}/${id}/${ResourceName.APPLICATION_PROFILE_PROMPT_DATA}`;

    const onDataChanged = (data: PromptDataEntity[]) => {
        if (onChange) {
            onChange(data);
        }
    };

    const onRowClick = (record: PromptDataEntity) => {
        navigate(
            `${BASE_PATH}/${ResourceName.APPLICATION_PROFILE_LISTS}/${appProfileId}/${ResourceName.APPLICATION_PROFILE_CARD_DEFINITION}/${id}/${ResourceName.APPLICATION_PROFILE_PROMPT_DATA}/${FORM_ACTION.EDIT}/${record.id}?promptDataProfile=${record.promptDataType}&type=${type}`,
        );
    };

    const [deleteTableItem] = usePromptDataDeleteMutation();

    const deleteCallback = (item: PromptDataEntity) => {
        deleteTableItem({
            appProfileId,
            cardProfId: id,
            id: item.id,
        })
            .unwrap()
            .then((payload) => performDeleteSuccess(payload))
            .catch(() => {
                dispatch(
                    showWarningNotification({
                        text: i18n.t("common.formDeleteError", {
                            id: item.id,
                        }),
                    }),
                );
            });

        const performDeleteSuccess = (payload: PromptDataEntity) => {
            if (payload) {
                notifyOkDelete(payload);
            }
            onDataChanged(value.filter((record) => record.id !== item.id));
        };
    };

    const notifyOkDelete = useCallback(
        (payload: PromptDataEntity) => {
            dispatch(
                showSuccessNotification({
                    text: i18n.t("common.formDeleteSuccess", {
                        id: payload.id,
                        resource: payload.name,
                    }),
                    autoCloseDisabled: false,
                }),
            );
        },
        [dispatch],
    );

    const [copyRequest] = useUpdateTableDataMutation();
    const baseURI = `${ResourceName.DATA_GROUPS}/${getAssignedDataGroup()?.code}`;

    const notifyOkCopy = useCallback(
        (payload: ExtendedAlertActionPayload) => {
            dispatch(
                showSuccessNotification({
                    text: i18n.t("common.formCopySuccess", {
                        id: payload.id,
                        resource: payload.name,
                    }),
                    autoCloseDisabled: false,
                }),
            );
        },
        [dispatch],
    );

    const copyCallback = (record: PromptDataEntity) => {
        copyRequest({
            url: `${baseURI}/${resourceUrl}/${record.id}/${FORM_ACTION.COPY}`,
            method: "POST",
        })
            .unwrap()
            .then((payload) => performCopySuccess(payload))
            .catch(() => {
                // error handled in rtkQueryErrorLogger
            });

        const performCopySuccess = (payload: AlertActionPayload | undefined) => {
            const payloadIdObject = payload as Entity;
            navigate(
                `${BASE_PATH}/${resourceUrl}/${FORM_ACTION.EDIT}/${payloadIdObject.id}?promptDataProfile=${record.promptDataType}&type=${type}`,
            );
            if (payload) {
                notifyOkCopy(payload);
            }
        };
    };

    return (
        <PropertyTable<PromptDataEntity>
            getColumnsConfig={getColumnsConfig}
            name="appProfilePromptDataTable"
            propertyAsRowKey="id"
            tableData={value || []}
            readOnly={readOnly || false}
            onDataChanged={onDataChanged}
            onRow={(record: PromptDataEntity) => {
                return {
                    onClick: () => onRowClick(record),
                };
            }}
            resourceUrl={resourceUrl}
            actions={[Actions.DELETE, Actions.COPY]}
            actionCallbacks={{ copy: copyCallback, delete: (item?: PromptDataEntity) => item && deleteCallback(item) }}
            customActions={[<PromptDataSelector key="promptDataSelector" cardProfileType={type} />]}
        />
    );
};
